/**
 * FinTS hooks (Phase 4, §7.3).
 *
 * `useFintsAvailability` gates every FinTS entry point in the UI (`enabled`
 * mirrors `FINTS_PRODUCT_ID` being configured server-side) and is cached like
 * the EB ASPSP list (1h `staleTime`).
 *
 * `useFintsConnect`/`useFintsReauth` create or repair a connection; a
 * completed (no-TAN) response may already carry upserted accounts, so both
 * invalidate `['connections']` + `['accounts']` on success. `useFintsSync`
 * mirrors `useSyncConnection`'s full downstream invalidation set, but only
 * when the response carries a finished `run` — a `challenge` response has not
 * written anything yet (the poll invalidates once IT completes).
 *
 * `useFintsChallengePoll` is the one new pattern in the app: a `useQuery`
 * whose `queryFn` is a POST (polling advances bank-side state, see
 * `docs/PHASE4-BUILD-SPEC.md` §5.4) — `refetchInterval: 3000`,
 * `refetchOnWindowFocus`/`refetchOnReconnect: false` (`useInbox.ts`'s
 * `refetchOnWindowFocus: false` is the in-repo precedent for "the cadence must
 * be driven solely by the interval/caller, not by ambient focus/reconnect
 * events"). Polling stops once a terminal status is observed; on `completed`
 * it invalidates the full downstream set exactly once (mirrors
 * `useConnectionMutations.ts::useSyncConnection`).
 */
import { useEffect, useRef } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '../api.ts';
import { qk } from '../queryKeys.ts';
import type {
  FintsAvailabilityOut,
  FintsChallengePollOut,
  FintsChallengeStatus,
  FintsConnectIn,
  FintsConnectOut,
  FintsReauthIn,
  FintsSyncOut,
  SyncTriggerIn,
} from '../types.ts';

/** Invalidate `['sync','runs',*]` regardless of params (mirrors useConnectionMutations.ts). */
const SYNC_RUNS_PREFIX = ['sync', 'runs'] as const;

/**
 * Invalidate the full sync-downstream cache set — mirrors
 * `useConnectionMutations.ts::useSyncConnection`'s invalidation list exactly
 * (connections, sync runs, accounts, transactions, inbox, reports, budgets).
 * Shared by every place a FinTS sync run finishes: `useFintsSync`'s
 * immediate (no-TAN) success and `useFintsChallengePoll`'s completion effect.
 */
function invalidateSyncDownstream(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: qk.connections });
  void qc.invalidateQueries({ queryKey: SYNC_RUNS_PREFIX });
  void qc.invalidateQueries({ queryKey: qk.accounts });
  void qc.invalidateQueries({ queryKey: qk.transactions });
  void qc.invalidateQueries({ queryKey: qk.inbox });
  void qc.invalidateQueries({ queryKey: qk.reports });
  void qc.invalidateQueries({ queryKey: qk.budgets });
}

/** GET /api/connections/fints/availability — gates every FinTS entry point. */
export function useFintsAvailability(): UseQueryResult<FintsAvailabilityOut, Error> {
  return useQuery({
    queryKey: qk.fintsAvailability,
    queryFn: () => api.get<FintsAvailabilityOut>('/api/connections/fints/availability'),
    staleTime: 3600_000,
  });
}

/** POST /api/connections/fints — start FinTS setup (preset or free-form bank). */
export function useFintsConnect(): UseMutationResult<FintsConnectOut, Error, FintsConnectIn> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: FintsConnectIn) => api.post<FintsConnectOut>('/api/connections/fints', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.connections });
      void qc.invalidateQueries({ queryKey: qk.accounts });
    },
  });
}

export interface FintsReauthVars {
  id: number;
  body: FintsReauthIn;
}

/** POST /api/connections/fints/{id}/reauth — TAN-only (stored PIN) or new-PIN re-auth. */
export function useFintsReauth(): UseMutationResult<FintsConnectOut, Error, FintsReauthVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: FintsReauthVars) =>
      api.post<FintsConnectOut>(`/api/connections/fints/${id}/reauth`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.connections });
      void qc.invalidateQueries({ queryKey: qk.accounts });
    },
  });
}

export interface FintsSyncVars {
  id: number;
  body?: SyncTriggerIn;
}

/**
 * POST /api/connections/fints/{id}/sync — attended manual sync or bootstrap.
 * A `challenge` in the response means nothing was written yet (wait for
 * `useFintsChallengePoll` to complete); a `run` means the sync finished
 * synchronously (no TAN required), so the full downstream set is invalidated
 * immediately, exactly like `useSyncConnection`.
 */
export function useFintsSync(): UseMutationResult<FintsSyncOut, Error, FintsSyncVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: FintsSyncVars) =>
      api.post<FintsSyncOut>(`/api/connections/fints/${id}/sync`, body ?? { trigger: 'manual' }),
    onSuccess: (data) => {
      if (!data.run) return;
      invalidateSyncDownstream(qc);
    },
  });
}

export interface UseFintsChallengePollOptions {
  /** Caller-side gate (e.g. only poll while a wait panel is mounted). Default `true`. */
  enabled?: boolean;
}

/** `pending` is the only non-terminal status — everything else stops the poll. */
function isTerminal(status: FintsChallengeStatus | undefined): boolean {
  return status != null && status !== 'pending';
}

/**
 * POST /api/connections/fints/challenges/{id}/poll, on a 3s interval, until a
 * terminal status (`completed`/`failed`/`expired`) is observed — polling then
 * stops on its own regardless of `options.enabled` (the challenge is dead
 * server-side; further polls would just replay the stored terminal result).
 * On `completed`, invalidates the full downstream set exactly once.
 */
export function useFintsChallengePoll(
  id: string,
  options: UseFintsChallengePollOptions = {},
): UseQueryResult<FintsChallengePollOut, Error> {
  const qc = useQueryClient();
  const invalidatedForRef = useRef<string | null>(null);

  const query = useQuery<FintsChallengePollOut, Error>({
    queryKey: qk.fintsChallenge(id),
    queryFn: () => api.post<FintsChallengePollOut>(`/api/connections/fints/challenges/${id}/poll`),
    enabled: (q) => (options.enabled ?? true) && !isTerminal(q.state.data?.status),
    refetchInterval: 3000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (query.data?.status === 'completed' && invalidatedForRef.current !== id) {
      invalidatedForRef.current = id;
      invalidateSyncDownstream(qc);
    }
  }, [id, query.data, qc]);

  return query;
}
